import { useLocation } from 'react-router';
import {
  Stack,
  Grid,
  Card,
  Tooltip,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { fDateDifference } from 'src/utils/format-time';
import PropTypes from 'prop-types';
import Label from 'src/components/label';
import ReactAudioPlayer from 'react-audio-player';
import styles from '../styles.module.css';

const InterviewResult = ({ interview, score }) => {
  // hooks
  const location = useLocation();

  // states
  const [copySuccess, setCopySuccess] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showEvalTooltop, setShowEvalTooltip] = useState('');

  // functions
  const handleCopyClick = interviewUrl => {
    navigator.clipboard.writeText(interviewUrl).then(
      () => {
        setCopySuccess(true);

        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      },
      () => {},
    );
  };

  const interviewDuration = (timestamp1, timestamp2) => {
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);

    const timeDifference = Math.abs(date2 - date1);

    const minutes = timeDifference / (1000 * 60);

    const formattedMinutes = minutes.toFixed(0);

    return `${formattedMinutes} min`;
  };

  const getScore = () => {
    if (interview && interview.interview_status === 'EVALUATED') {
      return score;
    }
    return 'Awaiting';
  };

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Stack component={Card} sx={{ mb: '2rem' }}>
      <Stack spacing={2} sx={{ p: 3, typography: 'body2' }}>
        <Grid container spacing={{ xs: 0.5, md: 2 }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '600',
              fontSize: '1rem',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Interview Link
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <div className={styles.interview_url}>
              <Button
                sx={{ marginLeft: '-15px' }}
                disabled={copySuccess}
                onClick={() =>
                  handleCopyClick(
                    process.env.REACT_APP_INTERVIEW_CLIENT_URL +
                      interview.interview_url,
                  )
                }
              >
                {copySuccess ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={{ xs: 0.5, md: 2 }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '600',
              fontSize: '1rem',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Candidate Email
            </Typography>
          </Grid>

          <Grid item xs={12} md={8} sx={{ color: 'text.primary' }}>
            <Typography variant="body1">{interview.email}</Typography>
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 0.5, md: 2 }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              color: 'text.primary',
              fontWeight: '600',
              fontSize: '1rem',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Attempted:
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} sx={{ color: 'text.primary' }}>
            <Typography variant="body1">
              {interview.interview_status === 'EVALUATED'
                ? fDateDifference(interview.attempted_at)
                : 'NA'}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 0.5, md: 2 }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              color: 'text.primary',
              fontWeight: '600',
              fontSize: '1rem',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Interview Duration:
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} sx={{ color: 'text.primary' }}>
            <Typography variant="body1">
              {interview.interview_status === 'EVALUATED'
                ? interviewDuration(interview.attempted_at, interview.ended_at)
                : 'NA'}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 0.5, md: 2 }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              color: 'text.primary',
              fontWeight: '600',
              fontSize: '1rem',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Score:
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" gap="2px" alignItems="baseline">
              <Typography variant="body1">{getScore()}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      <>
        {interview &&
          interview.questions.map((question, idx) => {
            let solution = null;

            if (interview.solutions.length) {
              solution = interview.solutions.find(
                item => question.question_id === item.question_id,
              );
            }

            const getTooltip = () => {
              if (solution) {
                if (!solution.is_evaluated) return 'Evaluating';
                return '';
              }
              return 'Not Attempted';
            };

            return (
              <Accordion
                key={idx}
                expanded={expanded === idx}
                disabled={!solution}
                onChange={handleChange(idx)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                >
                  <Grid
                    container
                    justifyContent="space-between"
                    spacing={0}
                    sx={{ width: '100%', marginRight: 3 }}
                  >
                    <Grid item xs={11}>
                      <Typography>
                        {`Question ${idx + 1}: ${question.text}`}
                      </Typography>
                    </Grid>
                    {getTooltip() && (
                      <Grid item xs={1}>
                        <Tooltip
                          open={showEvalTooltop === idx}
                          arrow
                          onMouseEnter={() => setShowEvalTooltip(idx)}
                          onMouseLeave={() => setShowEvalTooltip('')}
                          title="Report will be shown after evaluation"
                        >
                          <Label
                            sx={{ height: 20, fontSize: 10 }}
                            variant="soft"
                            color={
                              getTooltip() === 'Not Attempted'
                                ? 'warning'
                                : 'info'
                            }
                          >
                            {getTooltip()}
                          </Label>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </AccordionSummary>

                <AccordionDetails>
                  {solution && (
                    <Stack gap={2}>
                      {solution.is_evaluated && (
                        <Card sx={{ p: 3 }}>
                          <Stack gap={3}>
                            <Stack>
                              <Typography variant="h6">Result</Typography>
                              <Typography variant="body1">
                                {solution.score}/10
                              </Typography>
                            </Stack>
                            <Stack>
                              <Typography variant="h6">Remark</Typography>
                              <Typography variant="body1">
                                {solution.remark}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Card>
                      )}
                      <Card sx={{ p: 3 }}>
                        <Stack gap={4}>
                          <Stack gap={2}>
                            <Typography variant="h6">
                              Candidate&apos;s Audio Recording
                            </Typography>
                            <ReactAudioPlayer
                              src={solution.audio_url}
                              style={{ width: '40%', height: '40px' }}
                              controls
                            />
                          </Stack>
                          <Stack gap={2}>
                            <Typography variant="h6">Transcription</Typography>
                            <Typography variant="body1">
                              {solution.transcript !== ''
                                ? solution.transcript
                                : 'Response Not Found'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Card>
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </>
    </Stack>
  );
};

InterviewResult.propTypes = {
  interview: PropTypes.object,
  score: PropTypes.string,
};

export default InterviewResult;
